"use client";

import Container from "react-bootstrap/Container";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Button from "react-bootstrap/Button";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { useSelector } from "react-redux";
import { useEffect } from "react";

import {
  PowerSearchProvider,
  usePowerSearch,
} from "./contexts/power-search-context";
import { TabProvider, useTab } from "./contexts/TabContext";
import SearchContainer from "./SearchContainer";
import LegacyWorksheet from "./Worksheet";
import { getAllEntityMembers } from "./utils/searchUtils";
import {
  FullScreenProvider,
  useFullScreen,
} from "./contexts/FullScreenProvider";
import { mixpanelTrack } from "shared/trackEvents/mixpanelTracking";
import { isOpenRemunerationPlan } from "shared/plans";
import {
  POWER_SEARCH_EVENT_NAMES,
  WORKSPACE_EVENT_NAMES,
  MIXPANEL_PROPS,
} from "shared/trackEvents/constants";

const WorksheetTabContent = () => {
  const { paramsForWorksheet } = usePowerSearch();

  return (
    <div>
      <LegacyWorksheet
        getMembers={getAllEntityMembers}
        params={paramsForWorksheet}
      />
    </div>
  );
};

const PowerSearchContent = () => {
  const { isFullScreen, openFullScreen, closeFullScreen } = useFullScreen();

  const currentUserFromRedux = useSelector(
    (state) => state.users?.user || null
  );
  const { activeTab, setActiveTab } = useTab();

  const _handleTabChange = (tabName) => {
    if (currentUserFromRedux) {
      const isOpenRemUser = isOpenRemunerationPlan(currentUserFromRedux);

      if (tabName === "search") {
        const eventName = isOpenRemUser
          ? POWER_SEARCH_EVENT_NAMES.OR_POWER_SEARCH_PAGE_CLICKED
          : POWER_SEARCH_EVENT_NAMES.OD_POWER_SEARCH_PAGE_CLICKED;

        // Only track if window.mixpanel is available
        if (window.mixpanel && typeof window.mixpanel.track === "function") {
          mixpanelTrack(eventName, {
            [MIXPANEL_PROPS.TAB_NAME]: "Power Search",
            userEmail: currentUserFromRedux?.email,
          });
        }
      } else if (tabName === "worksheet") {
        const eventName = isOpenRemUser
          ? WORKSPACE_EVENT_NAMES.OR_WORKSPACE_PAGE_CLICKED
          : WORKSPACE_EVENT_NAMES.OD_WORKSPACE_PAGE_CLICKED;

        // Only track if window.mixpanel is available
        if (window.mixpanel && typeof window.mixpanel.track === "function") {
          mixpanelTrack(eventName, {
            [MIXPANEL_PROPS.TAB_NAME]: "Work Space",
            userEmail: currentUserFromRedux?.email,
          });
        }
      }
    }

    setActiveTab(tabName);
  };

  const _handleFullScreenClick = (isOpening) => {
    if (currentUserFromRedux) {
      const isOpenRemUser = isOpenRemunerationPlan(currentUserFromRedux);
      const eventName = isOpenRemUser
        ? POWER_SEARCH_EVENT_NAMES.OR_POWER_SEARCH_FULL_SCREEN_CLICKED
        : POWER_SEARCH_EVENT_NAMES.OD_POWER_SEARCH_FULL_SCREEN_CLICKED;

      // Only track if window.mixpanel is available
      if (window.mixpanel && typeof window.mixpanel.track === "function") {
        mixpanelTrack(eventName, {
          [MIXPANEL_PROPS.TAB_NAME]: isOpening
            ? "Open Fullscreen"
            : "Exit Fullscreen",
          userEmail: currentUserFromRedux?.email,
        });
      }
    }

    if (isOpening) {
      openFullScreen();
    } else {
      closeFullScreen();
    }
  };

  return (
    <PowerSearchProvider currentUser={currentUserFromRedux}>
      <div className={`${isFullScreen ? "fullscreen-mode" : "normal-mode"}`}>
        <Container style={{ maxWidth: "100%" }} fluid>
          <h3
            className={`font-weight-bold text-nowrap ${
              isFullScreen ? "ml-3 mt-3" : ""
            }`}
            style={{ color: "#001F38" }}
          >
            Power Search
          </h3>

          <div className="text-center">
            <ButtonGroup
              className={`mb-3 d-flex justify-content-center ${
                isFullScreen ? "mb-3" : ""
              }`}
              style={{
                width: "80%",
                margin: "0 auto",
                paddingLeft: "10rem",
                paddingRight: "10rem",
              }}
            >
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="search_tab">Search</Tooltip>}
              >
                <Button
                  variant={
                    activeTab === "search" ? "primary" : "outline-primary"
                  }
                  onClick={() => _handleTabChange("search")}
                  style={{
                    pointerEvents: activeTab === "search" ? "none" : "auto",
                  }}
                >
                  Power Search
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="worksheet_tab">Work Space</Tooltip>}
              >
                <Button
                  variant={
                    activeTab === "worksheet" ? "primary" : "outline-primary"
                  }
                  onClick={() => _handleTabChange("worksheet")}
                  style={{
                    pointerEvents: activeTab === "worksheet" ? "none" : "auto",
                  }}
                >
                  Work Space
                </Button>
              </OverlayTrigger>
            </ButtonGroup>
          </div>
          {!isFullScreen ? (
            <button
              onClick={() => _handleFullScreenClick(true)}
              style={{
                position: "absolute",
                top: 148,
                right: 32,
                zIndex: 20,
                background: "#fff",
                border: "1px solid #ccc",
                borderRadius: 6,
                padding: "6px 16px",
                fontWeight: 500,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                cursor: "pointer",
                transition: "box-shadow 0.2s",
              }}
              aria-label="Open Fullscreen"
            >
              <i className="fa fa-expand" style={{ marginRight: 6 }} />
              Fullscreen
            </button>
          ) : (
            <button
              onClick={() => _handleFullScreenClick(false)}
              style={{
                position: "absolute",
                top: 4,
                right: 10,
                zIndex: 20,
                background: "#fff",
                border: "1px solid #ccc",
                borderRadius: 6,
                padding: "6px 16px",
                fontWeight: 500,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                cursor: "pointer",
                transition: "box-shadow 0.2s",
              }}
            >
              <i className="fa fa-compress" style={{ marginRight: 6 }} />
              Exit Fullscreen
            </button>
          )}

          {/* Tab Content */}
          {activeTab === "search" && <SearchContainer />}
          {activeTab === "worksheet" && <WorksheetTabContent />}
        </Container>
      </div>
    </PowerSearchProvider>
  );
};

const PowerSearch = () => {
  return (
    <FullScreenProvider>
      <TabProvider defaultTab="search">
        <PowerSearchContent />
      </TabProvider>
    </FullScreenProvider>
  );
};

export default PowerSearch;
